import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { ObscuredData } from '@se/components';
import {
    AuthData,
    EcgExamination,
    EcgNotification,
    IEcgApproveRequest,
    IEcgExaminationWorklistDTO,
    IEcgHistorySearchParam,
    IEcgReport,
    IPreviousEcg,
    ISissCloudUploadRequest,
    ISissWayCloudUploadRequest,
    IVisitWorklistSearchingParams,
} from '@se/models';
import { Observable } from 'rxjs';
import { ISeSignResultData, SeSignInfo, SissData } from '../../../models/estensasign.model';
import { ServerRootService } from '../../../utils/routing/serverroot.service';
import { HardCodedDataUpdate, IResponseStatus, UnlockData } from '../../report-workflow-exam.service';

export abstract class EcgBaseService extends ServerRootService {
    protected readonly httpClient = inject(HttpClient);

    getEcgWorklist(searchParams: IVisitWorklistSearchingParams) {
        return this.httpClient.post<IEcgExaminationWorklistDTO[]>(
            this.getServerRoot() + '/ECG/GetEcgWorklist',
            searchParams,
            this.buildOptions(),
        );
    }

    getEcgNotifications(ignoreTokenErrors: boolean = false) {
        return this.httpClient.get<EcgNotification[]>(
            this.getServerRoot() + '/ECG/GetActiveAlarms',
            this.buildOptions(null, ignoreTokenErrors),
        );
    }

    getEcgReportingDetails(examinationId: string) {
        return this.httpClient.get<IEcgReport>(
            this.getServerRoot() + '/ECG/GetEcgReportingDetails?examinationId=' + examinationId,
            this.buildOptions(),
        );
    }

    getPreviousEcgs(ecgHistorySearchParam: IEcgHistorySearchParam) {
        return this.httpClient.post<IPreviousEcg[]>(
            this.getServerRoot() + '/ECG/RetrievePreviousEcgs',
            ecgHistorySearchParam,
            this.buildOptions(),
        );
    }

    notifyAlarmWarningsAccepted(idExaminations: string[]) {
        return this.httpClient.put(
            this.getServerRoot() + '/ECG/NotifyAlarmWarningsAccepted',
            { idExaminations },
            this.buildOptions(),
        );
    }

    notifyExamsDeleting(idExaminations: string[]) {
        return this.httpClient.put(
            this.getServerRoot() + '/ECG/NotifyExamsDeleting',
            { idExaminations },
            this.buildOptions(),
        );
    }

    /*@TODO tipizzare reports:any[]...*/
    saveEcgReports(reports: any[]) {
        return this.httpClient.post<IResponseStatus<IEcgReport>[]>(
            this.getServerRoot() + '/ECG/SaveEcgReports',
            reports,
            this.buildOptions(),
        );
    }

    getEcgBaseTrace(params: string) {
        return this.httpClient.get(this.getServerRoot() + '/ECG/GetEcgData' + params, this.buildOptions());
    }

    approveBase(requestBody: IEcgApproveRequest, authData: AuthData | null = null) {
        return this.httpClient.post<IResponseStatus[]>(
            this.getServerRoot() + '/ECG/Approve',
            requestBody,
            this.buildOptions(authData),
        );
    }

    undoValidation(reportId: string, authData: AuthData, isFromWorklist?: boolean) {
        return this.httpClient.post(
            this.getServerRoot() + '/ECG/UndoApprove',
            { reportId, isFromWorklist },
            this.buildOptions(authData),
        );
    }

    forceUnlockMultiple(data: UnlockData[]) {
        return this.httpClient.put(this.getServerRoot() + '/ECG/ForceUnlockMultiple', data, this.buildOptions());
    }

    unlock(data: UnlockData[]) {
        return this.httpClient.put(this.getServerRoot() + '/ECG/Unlock', data, this.buildOptions());
    }

    getPdf(info: SeSignInfo) {
        return this.httpClient.post(this.getServerRoot() + '/ECG/GetPdfEcgReport', info, this.buildOptions());
    }

    lockExams(exams: any) {
        return this.httpClient.post(this.getServerRoot() + '/ECG/LockExams', exams, this.buildOptions());
    }

    merge(IdExaminationSource: string, IdExaminationTarget: string) {
        return this.httpClient.post<boolean>(
            this.getServerRoot() + '/ECG/MergeReport',
            {
                IdExaminationSource,
                IdExaminationTarget,
            },
            this.buildOptions(),
        );
    }

    undoMerge(examinationId: string) {
        return this.httpClient.post<boolean>(
            this.getServerRoot() + '/ECG/UndoMergeReport',
            examinationId,
            this.buildOptions(),
        );
    }

    getDataForSissSign(examinationIds: string): Observable<SissData> {
        return this.httpClient.get<SissData>(
            this.getServerRoot() + '/ECG/GetSissData/?examinationId=' + examinationIds, //quindi sono degli id gia concatenati? tipizzare in string???
            this.buildOptions(),
        );
    }

    uploadReportToSissCloudBase(requestBody: ISissCloudUploadRequest) {
        return this.httpClient.post<ISeSignResultData[]>(
            `${this.getServerRoot()}/ECG/UploadReportToSissCloud`,
            requestBody,
            this.buildOptions(),
        );
    }

    uploadReportToSissWayCloudBase(requestBody: ISissWayCloudUploadRequest) {
        return this.httpClient.post<ISeSignResultData[]>(
            `${this.getServerRoot()}/ECG/UploadReportToSissWayCloud`,
            requestBody,
            this.buildOptions(),
        );
    }

    saveHardCodedData(update: HardCodedDataUpdate) {
        return this.httpClient.post<ObscuredData>(
            this.getServerRoot() + '/ECG/SaveHardCodedData',
            update,
            this.buildOptions(),
        );
    }

    deleteExaminations(ids: string[]) {
        return this.httpClient.put(this.getServerRoot() + '/ECG/DeleteExaminations', ids, this.buildOptions());
    }

    sign(info: SeSignInfo) {
        return this.httpClient.post<ISeSignResultData[]>(
            this.getServerRoot() + '/ECG/SignEcgReport',
            info,
            this.buildOptions(),
        );
    }

    requestLinkedEcgs(idExaminations: string[]) {
        return this.httpClient.post<EcgExamination[]>(
            this.getServerRoot() + '/ECG/GetLinkedEcgs',
            { idExaminations },
            this.buildOptions(),
        );
    }

    getPreviewEcgPdf(examinationId: string) {
        return this.httpClient.get<string>(
            this.getServerRoot() + '/ECG/GetPreviewEcgPdf?examinationId=' + examinationId,
            this.buildOptions(),
        );
    }

    getEcgPdf(reportIds: string[], displayedSettings: any, ignoreErrors: boolean = false) {
        return this.httpClient.post<string>(
            this.getServerRoot() + '/ECG/GetPdf',
            { reportIds, displayedSettings },
            this.buildOptions(null, ignoreErrors),
        );
    }
}
